import React from 'react';
import { MapPinIcon, UserIcon, SearchIcon, LayersIcon, XIcon, CompassIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
type MapViewProps = {
  locations: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    postCount: number;
  }[];
  currentLocation?: {
    lat: number;
    lng: number;
  };
};
export const MapView: React.FC<MapViewProps> = ({
  locations,
  currentLocation
}) => {
  return <div className="relative bg-[#e8ecef] rounded-xl h-[calc(100vh-200px)] overflow-hidden" data-id="element-164">
      {/* Static map background */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80')] bg-cover opacity-50" data-id="element-165"></div>
      {/* Map search overlay */}
      <div className="absolute top-4 left-4 right-4 z-10" data-id="element-166">
        <div className="relative" data-id="element-167">
          <input type="text" placeholder="Search dive sites..." className="w-full p-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white/90 backdrop-blur-sm shadow-lg" data-id="element-168" />
          <SearchIcon size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-169" />
        </div>
      </div>
      {/* Map elements overlay */}
      <div className="absolute inset-0" data-id="element-170">
        {/* Current location marker */}
        {currentLocation && <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-171">
            <div className="w-10 h-10 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center animate-pulse" data-id="element-172">
              <UserIcon size={18} className="text-white" data-id="element-173" />
            </div>
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap bg-white px-2 py-1 rounded text-xs font-medium shadow-lg" data-id="element-174">
              Your Location
            </div>
          </div>}
        {/* Location pins */}
        {locations.map((location, index) => {
        // Calculate random positions for demo purposes
        const top = 20 + Math.random() * 60;
        const left = 20 + Math.random() * 60;
        return <div key={index} className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 group" style={{
          top: `${top}%`,
          left: `${left}%`
        }} data-id="element-175">
              <div className="flex flex-col items-center" data-id="element-176">
                <div className="relative" data-id="element-177">
                  <div className="w-10 h-10 flex items-center justify-center" data-id="element-178">
                    <div className="absolute w-10 h-10 bg-primary/20 rounded-full animate-ping" data-id="element-179"></div>
                    <MapPinIcon size={30} className="text-primary relative z-10" data-id="element-180" />
                  </div>
                  {location.postCount > 0 && <div className="absolute -top-1 -right-1 bg-secondary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center border border-white z-20 font-bold" data-id="element-181">
                      {location.postCount}
                    </div>}
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap mt-1 font-medium" data-id="element-182">
                  {location.name}
                </div>
              </div>
            </div>;
      })}
      </div>
      {/* Map controls */}
      <div className="absolute top-20 right-4 flex flex-col space-y-2" data-id="element-183">
        <button className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" data-id="element-184">
          <span className="text-xl font-medium" data-id="element-185">+</span>
        </button>
        <button className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" data-id="element-186">
          <span className="text-xl font-medium" data-id="element-187">−</span>
        </button>
        <button className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" data-id="element-188">
          <LayersIcon size={18} data-id="element-189" />
        </button>
        <button className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors" data-id="element-190">
          <CompassIcon size={18} data-id="element-191" />
        </button>
      </div>
      {/* Location details card */}
      <Card className="absolute bottom-4 left-4 right-4 max-w-md mx-auto" data-id="element-192">
        <div className="flex justify-between items-start" data-id="element-193">
          <div data-id="element-194">
            <h2 className="font-semibold" data-id="element-195">Great Barrier Reef</h2>
            <p className="text-sm text-gray-600" data-id="element-196">5 km away • 24 posts</p>
          </div>
          <button className="p-1 text-gray-500 hover:text-gray-700" data-id="element-197">
            <XIcon size={18} data-id="element-198" />
          </button>
        </div>
        <div className="flex gap-3 mt-3" data-id="element-199">
          <img src="https://images.unsplash.com/photo-1682687982167-d7fb3ed8541d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80" alt="Great Barrier Reef" className="w-20 h-20 object-cover rounded-lg" data-id="element-200" />
          <div className="flex-1" data-id="element-201">
            <div className="flex items-center mb-1" data-id="element-202">
              <div className="flex" data-id="element-203">
                {[...Array(5)].map((_, i) => <svg key={i} className={`w-4 h-4 ${i < 4 ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" data-id="element-204">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" data-id="element-205" />
                  </svg>)}
              </div>
              <span className="text-sm ml-1" data-id="element-206">4.8 (42 reviews)</span>
            </div>
            <p className="text-xs text-gray-600 mb-2" data-id="element-207">
              Famous for its colorful coral formations and diverse marine life.
            </p>
            <div className="grid grid-cols-2 gap-1 text-xs" data-id="element-208">
              <div className="flex items-center" data-id="element-209">
                <span className="w-2 h-2 rounded-full bg-green-500 mr-1" data-id="element-210"></span>
                <span data-id="element-211">Excellent conditions</span>
              </div>
              <div className="flex items-center" data-id="element-212">
                <span className="w-2 h-2 rounded-full bg-blue-500 mr-1" data-id="element-213"></span>
                <span data-id="element-214">26°C water temp</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100" data-id="element-215">
          <Button variant="primary" fullWidth data-id="element-216">
            View Details
          </Button>
          <Button variant="outline" fullWidth data-id="element-217">
            Directions
          </Button>
        </div>
      </Card>
      <div className="absolute bottom-4 right-4 bg-white rounded-md shadow-lg p-3 text-xs" data-id="element-218">
        <div className="flex items-center space-x-2" data-id="element-219">
          <div className="w-3 h-3 rounded-full bg-primary" data-id="element-220"></div>
          <span data-id="element-221">Dive Sites</span>
        </div>
        <div className="flex items-center space-x-2 mt-2" data-id="element-222">
          <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-223"></div>
          <span data-id="element-224">Your Location</span>
        </div>
        <div className="flex items-center space-x-2 mt-2" data-id="element-225">
          <div className="w-3 h-3 rounded-full bg-secondary" data-id="element-226"></div>
          <span data-id="element-227">Popular Spots</span>
        </div>
      </div>
    </div>;
};